import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { isOverdue } from '../../taskFilters'
import { TASK_STATUSES, TASK_TYPES, type Project } from '../../types'
import { useStaffLookup } from '../../ui'
import { ProgressBar } from '../ProjectBits'

const C = { primary: 'var(--chart-1)', secondary: 'var(--chart-3)', tertiary: 'var(--chart-4)' } as const
const tooltipStyle = { background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--popover-foreground)' }
const axis = { fontSize: 11, fill: 'var(--muted-foreground)' }

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  )
}

function ChartCard({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border bg-card p-4 shadow-card">
      <h3 className="font-semibold">{title}</h3>
      {hint ? <p className="mb-3 text-xs text-muted-foreground">{hint}</p> : null}
      <div className="h-64">{children}</div>
    </section>
  )
}

export function ReportsTab({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const tasks = project.tasks
  const nf = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 })

  const data = useMemo(() => {
    const byStatus = TASK_STATUSES.map((s) => ({ name: t(`projects.taskStatus.${s}`), value: tasks.filter((x) => x.status === s).length }))
    const byType = TASK_TYPES.map((ty) => ({ name: t(`projects.taskType.${ty}`), open: tasks.filter((x) => x.type === ty && x.status !== 'done').length, done: tasks.filter((x) => x.type === ty && x.status === 'done').length }))
    const people = project.team.map((m) => {
      const mine = tasks.filter((x) => x.assigneeId === m.userId)
      return {
        name: nameOf(m.userId).split(' ')[0] ?? '',
        fullName: nameOf(m.userId),
        open: mine.filter((x) => x.status !== 'done').length,
        done: mine.filter((x) => x.status === 'done').length,
        estimate: mine.reduce((s, x) => s + x.estimateHours, 0),
        spent: mine.reduce((s, x) => s + x.spentHours, 0),
        overdue: mine.filter(isOverdue).length,
      }
    })
    return { byStatus, byType, people }
  }, [tasks, project.team, nameOf, t])

  const done = tasks.filter((x) => x.status === 'done').length
  const estimate = tasks.reduce((s, x) => s + x.estimateHours, 0)
  const spent = tasks.reduce((s, x) => s + x.spentHours, 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile label={t('projects.reports.completion')} value={`%${project.progress}`} hint={t('projects.reports.completionHint', { done, total: tasks.length })} />
        <Tile label={t('projects.overview.overdue')} value={String(tasks.filter(isOverdue).length)} />
        <Tile label={t('projects.reports.hours')} value={`${nf.format(spent)} / ${nf.format(estimate)}`} hint={t('projects.reports.hoursHint')} />
        <Tile label={t('projects.overview.activeInstallations')} value={String(project.activeInstallations)} hint={t('projects.reports.installHint', { count: project.installations.length })} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard title={t('projects.reports.byStatus')} hint={t('projects.reports.byStatusHint')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byStatus} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid horizontal={false} stroke="var(--border)" />
              <XAxis type="number" allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={96} tick={axis} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--muted)' }} />
              <Bar dataKey="value" name={t('projects.reports.taskCount')} fill={C.primary} radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title={t('projects.reports.byType')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byType} margin={{ right: 8 }}>
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} width={28} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--muted)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="open" name={t('projects.reports.open')} fill={C.primary} radius={[4, 4, 0, 0]} stroke="var(--card)" strokeWidth={2} />
              <Bar dataKey="done" name={t('projects.reports.done')} fill={C.secondary} radius={[4, 4, 0, 0]} stroke="var(--card)" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title={t('projects.reports.hoursByMember')} hint={t('projects.reports.hoursHint')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.people} margin={{ right: 8 }}>
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={32} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--muted)' }} labelFormatter={(_, p) => p?.[0]?.payload?.fullName ?? ''} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="estimate" name={t('projects.tasks.fields.estimate')} fill={C.primary} radius={[4, 4, 0, 0]} stroke="var(--card)" strokeWidth={2} />
              <Bar dataKey="spent" name={t('projects.tasks.fields.spent')} fill={C.tertiary} radius={[4, 4, 0, 0]} stroke="var(--card)" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <section className="rounded-xl border bg-card p-4 shadow-card">
          <h3 className="mb-3 font-semibold">{t('projects.reports.installations')}</h3>
          {project.installations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('projects.install.empty')}</p>
          ) : (
            <ul className="space-y-3">
              {project.installations.map((i) => (
                <li key={i.id} className="space-y-1">
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="truncate font-medium">{i.customer}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{t(`projects.installStatus.${i.status}`)}</span>
                  </div>
                  <ProgressBar value={i.progress} label={i.customer} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="@container overflow-hidden rounded-xl border bg-card shadow-card">
        <h3 className="border-b px-4 py-3 font-semibold">{t('projects.reports.memberTable')}</h3>
        <ul className="divide-y @2xl:hidden">
          {data.people.map((p) => (
            <li key={p.fullName} className="space-y-2 p-3">
              <p className="font-medium">{p.fullName}</p>
              <dl className="grid grid-cols-3 gap-2 text-center">
                {[
                  [t('projects.reports.open'), p.open],
                  [t('projects.reports.done'), p.done],
                  [t('projects.overview.overdue'), p.overdue],
                  [t('projects.tasks.fields.estimate'), nf.format(p.estimate)],
                  [t('projects.tasks.fields.spent'), nf.format(p.spent)],
                ].map(([label, value]) => (
                  <div key={String(label)} className="min-w-0 rounded-lg bg-muted/40 px-2 py-1.5">
                    <dt className="truncate text-[11px] text-muted-foreground">{label}</dt>
                    <dd className="text-sm font-semibold tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
        <div className="hidden @2xl:block">
          <Table>
            <THead>
              <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
                <TH>{t('projects.tasks.fields.assignee')}</TH>
                <TH className="text-right">{t('projects.reports.open')}</TH>
                <TH className="text-right">{t('projects.reports.done')}</TH>
                <TH className="text-right">{t('projects.overview.overdue')}</TH>
                <TH className="text-right">{t('projects.tasks.fields.estimate')}</TH>
                <TH className="text-right">{t('projects.tasks.fields.spent')}</TH>
              </TR>
            </THead>
            <TBody>
              {data.people.map((p) => (
                <TR key={p.fullName}>
                  <TD className="font-medium">{p.fullName}</TD>
                  <TD className="text-right tabular-nums">{p.open}</TD>
                  <TD className="text-right tabular-nums">{p.done}</TD>
                  <TD className="text-right tabular-nums">{p.overdue}</TD>
                  <TD className="text-right tabular-nums">{nf.format(p.estimate)}</TD>
                  <TD className="text-right tabular-nums">{nf.format(p.spent)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </section>
    </div>
  )
}
